import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

type StepperVariant = "v1" | "v2";

interface StepperSelectors {
  decrease: string;
  increase: string;
  value: string;
}

const SELECTORS_BY_VARIANT: Record<StepperVariant, StepperSelectors> = {
  v1: {
    decrease: ".core-stepper__button--decrease",
    increase: ".core-stepper__button--increase",
    value: ".core-stepper__value",
  },
  v2: {
    decrease: ".coreStepperV2__button--decrease",
    increase: ".coreStepperV2__button--increase",
    value: ".coreStepperV2__value",
  },
};

function resolveStepperSelectors(element: HTMLElement): StepperSelectors {
  const variant: StepperVariant = element.classList.contains("coreStepperV2")
    ? "v2"
    : "v1";
  return SELECTORS_BY_VARIANT[variant];
}

/**
 * Actualiza data-value, texto y estado disabled del stepper (v1/v2) sin disparar eventos.
 * Útil para sincronizar desde el calendario u otras fuentes externas.
 */
export function applyStepperValueToElement(
  element: HTMLElement,
  value: number,
): number {
  const selectors = resolveStepperSelectors(element);
  const min = parseInt(element.dataset.min || "0", 10);
  const max = parseInt(element.dataset.max || "100", 10);
  const clamped = Math.min(max, Math.max(min, value));
  element.dataset.value = String(clamped);

  const valueDisplay = element.querySelector(selectors.value);
  if (valueDisplay) {
    valueDisplay.textContent = String(clamped);
  }

  const decreaseBtn = element.querySelector<HTMLButtonElement>(
    selectors.decrease,
  );
  const increaseBtn = element.querySelector<HTMLButtonElement>(
    selectors.increase,
  );

  if (decreaseBtn) {
    decreaseBtn.disabled = clamped <= min;
  }
  if (increaseBtn) {
    increaseBtn.disabled = clamped >= max;
  }

  return clamped;
}

export class Stepper implements Plugin {
  private min: number;
  private max: number;
  private value: number;

  private readonly decreaseBtn: HTMLButtonElement | null;
  private readonly increaseBtn: HTMLButtonElement | null;
  private readonly valueDisplay: HTMLElement | null;

  constructor(private readonly element: HTMLElement) {
    this.min = parseInt(element.dataset.min || "0", 10);
    this.max = parseInt(element.dataset.max || "100", 10);
    this.value = parseInt(element.dataset.value || "0", 10);

    const selectors = resolveStepperSelectors(this.element);
    this.decreaseBtn = this.queryButton(selectors.decrease);
    this.increaseBtn = this.queryButton(selectors.increase);
    this.valueDisplay = this.queryValue(selectors.value);
  }

  init(): void {
    this.updateUI();
    this.setupEvents();
  }

  private setupEvents(): void {
    this.decreaseBtn?.addEventListener("click", () => this.handleDecrease());
    this.increaseBtn?.addEventListener("click", () => this.handleIncrease());
  }

  private queryButton(selector: string): HTMLButtonElement | null {
    return this.element.querySelector(selector);
  }

  private queryValue(selector: string): HTMLElement | null {
    return this.element.querySelector(selector);
  }

  private syncFromDataset(): void {
    this.min = parseInt(this.element.dataset.min || "0", 10);
    this.max = parseInt(this.element.dataset.max || "100", 10);
    this.value = parseInt(this.element.dataset.value || "0", 10);
  }

  private handleDecrease(): void {
    this.syncFromDataset();
    if (this.value > this.min) {
      this.updateValue(this.value - 1, "decrease");
    }
  }

  private handleIncrease(): void {
    this.syncFromDataset();
    if (this.value < this.max) {
      this.updateValue(this.value + 1, "increase");
    }
  }

  private updateValue(newValue: number, action: "increase" | "decrease"): void {
    this.syncFromDataset();
    const oldValue = this.value;
    this.value = applyStepperValueToElement(this.element, newValue);
    this.dispatchChange(oldValue, action);
  }

  private updateUI(): void {
    this.syncFromDataset();
    this.value = applyStepperValueToElement(this.element, this.value);
  }

  private dispatchChange(
    oldValue: number,
    action: "increase" | "decrease",
  ): void {
    this.element.dispatchEvent(
      new CustomEvent("stepperChange", {
        detail: {
          value: this.value,
          oldValue,
          action,
        },
        bubbles: true,
      }),
    );
  }
}

PluginRegistry.register("stepper", Stepper);
